// src/auth/AuthContext.js
// Fetches SWA's /.auth/me then /api/diConfig to build full auth + app context.
// Exposes: { ready, email, realEmail, impersonating, userRoles, isAdmin,
//           isSteering, periods, startImpersonating, stopImpersonating, reload }
// `periods` = the caller's applicable ACTIVE periods (office/department gated),
// each with its own visible `sections`. Section pages read the period code from
// the route (usePeriod) rather than from here.
//
// When an admin picks a user to impersonate, the global authFetch wrapper
// starts attaching `x-impersonate-email` on every /api/* call, and we
// re-bootstrap so the context reflects the target user's identity.

import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { DEBUG } from '../debugFlag';
import { installAuthFetch, setImpersonationEmail } from './authFetch';
import { loadDiUsers } from './diUsersCache';

const STORAGE_KEY = 'diImpersonating';

const EMPTY_STATE = {
  ready:        false,
  email:        null,
  realEmail:    null,
  impersonating: null,
  userRoles:    [],
  isAdmin:      false,
  isSteering:   false,
  isBillingTeam: false,
  billingTeamMember: false,
  // Concurrent active periods: the caller's APPLICABLE active periods, each
  // gated by office/department. Each entry carries its own visible sections:
  //   { period, label, periodActive, description, startDate, endDate, sections }
  periods:      [],
  diPublicUrl:  '',
  // true when diConfig failed after retries — distinguishes a load failure
  // from a legitimately inactive period so UX can show a retry prompt.
  configError:  false,
};

const AuthCtx = createContext({
  ...EMPTY_STATE,
  startImpersonating: () => {},
  stopImpersonating:  () => {},
  reload:             () => {},
});

export const useAuth = () => useContext(AuthCtx);

// Resolve the signed-in email from the SWA principal. Prefer the token CLAIMS
// (preferred_username / emails / email / .../emailaddress) over `userDetails`,
// which SWA sometimes returns MASKED (e.g. "de*****@bmss.com"). Mirrors the
// server's getEmail (api/shared/auth.js) so client + server agree on identity.
function emailFromPrincipal(p) {
  if (!p) return null;
  const claims = Array.isArray(p.claims) ? p.claims : [];
  const c =
    claims.find(x => x.typ === 'preferred_username') ||
    claims.find(x => x.typ === 'emails') ||
    claims.find(x => x.typ === 'email') ||
    claims.find(x => String(x.typ || '').endsWith('/emailaddress'));
  return ((c && c.val) || p.userDetails || '').toLowerCase() || null;
}

// Install the fetch wrapper once at module load so even early fetches get it.
installAuthFetch();

export function AuthProvider({ children }) {
  const [state, setState] = useState(EMPTY_STATE);
  // impersonating email persists across reloads via sessionStorage so the
  // admin doesn't lose context on a refresh.
  const [impersonating, setImpersonating] = useState(() => {
    try { return sessionStorage.getItem(STORAGE_KEY) || null; } catch { return null; }
  });

  // Monotonic token used to discard stale bootstrap results. Without this,
  // a slow Bootstrap-A (no impersonation) can resolve after a faster
  // Bootstrap-B (impersonating X) and overwrite the impersonated view with
  // the admin's — manifesting as "impersonation didn't take" until refresh.
  const bootstrapTokenRef = useRef(0);

  // `silent` (a background refresh — Admin save, heartbeat config change): keep the
  // current view on failure instead of showing the connection-error screen.
  const bootstrap = useCallback(async ({ silent = false } = {}) => {
    const myToken = ++bootstrapTokenRef.current;
    const isStale = () => myToken !== bootstrapTokenRef.current;

    try {
      // 1. SWA identity (real caller)
      const meRes = await fetch('/.auth/me', { credentials: 'include', cache: 'no-store' });
      if (isStale()) return;
      const { clientPrincipal } = await meRes.json().catch(() => ({}));
      const realEmail = emailFromPrincipal(clientPrincipal);

      if (DEBUG) console.debug('[AUTH] principal', { realEmail, clientPrincipal });

      if (!realEmail) {
        if (isStale()) return;
        setState({ ...EMPTY_STATE, ready: true });
        return;
      }

      // 2. App config — this call picks up any impersonation header and
      // returns the EFFECTIVE identity's view. MUST bypass the HTTP cache:
      // the response varies by the x-impersonate-email header, which the
      // browser ignores for cache keying — without no-store, entering "View
      // As" would re-serve the admin's cached (all-periods) config and the
      // target's office/department + section gating wouldn't take effect.
      //
      // Retry up to 2x on 5xx (transient DB ECONNRESET / Function App cold
      // start) before giving up. Without this a single transient failure sets
      // periods:[] and shows PeriodClosed to users on section URLs.
      let configRes;
      for (let attempt = 0; attempt < 3; attempt++) {
        if (attempt > 0) await new Promise(r => setTimeout(r, attempt * 2000));
        if (isStale()) return;
        configRes = await fetch('/api/diConfig', { credentials: 'include', cache: 'no-store' });
        if (isStale()) return;
        if (configRes.ok || configRes.status < 500) break;
      }
      if (!configRes.ok) throw new Error(`diConfig ${configRes.status}`);
      const config = await configRes.json();
      if (isStale()) return;

      if (DEBUG) console.debug('[AUTH] diConfig', config);

      setState({
        ready:        true,
        email:        (config.email || realEmail).toLowerCase(),
        realEmail,
        impersonating: impersonating || null,
        userRoles:    config.userRoles    || [],
        isAdmin:      config.isAdmin      || false,
        isSteering:   config.isSteering   || false,
        isBillingTeam: config.isBillingTeam || false,
        billingTeamMember: config.billingTeamMember || false,
        periods:      config.periods || [],
        diPublicUrl:  config.diPublicUrl  || '',
        configError:  false,
      });

      // Pre-warm the diUsers roster the first time an admin loads the app.
      // The shared cache guarantees this fires at most once per page load,
      // even across impersonation-driven re-bootstraps.
      if (config.isAdmin) {
        loadDiUsers()
          .then(() => { if (DEBUG) console.debug('[AUTH] diUsers pre-warmed'); })
          .catch(err => { if (DEBUG) console.debug('[AUTH] diUsers pre-warm failed', err); });
      }
    } catch (err) {
      if (isStale()) return;
      console.error('[AUTH] bootstrap error', err);
      if (silent) return;
      setState({ ...EMPTY_STATE, ready: true, configError: true });
    }
  }, [impersonating]);

  // Sync the impersonation header, then RE-BOOTSTRAP. This also covers the
  // initial mount (impersonating starts from sessionStorage). We must refetch
  // diConfig on an impersonation change because the APPLICABLE active periods
  // and their visible sections are office/department-gated — i.e. they depend
  // on the effective user, not just the realEmail. (Set the header first so the
  // diConfig fetch carries x-impersonate-email.)
  useEffect(() => {
    setImpersonationEmail(impersonating);
    bootstrap();
  }, [impersonating, bootstrap]);

  const startImpersonating = useCallback((email) => {
    const em = (email && String(email).toLowerCase().trim()) || null;
    if (!em) return;
    try { sessionStorage.setItem(STORAGE_KEY, em); } catch {}
    setImpersonating(em);
  }, []);

  const stopImpersonating = useCallback(() => {
    try { sessionStorage.removeItem(STORAGE_KEY); } catch {}
    setImpersonating(null);
  }, []);

  // Re-read diConfig (periods + visible sections). reload({ silent: true }) refreshes
  // in the background without a loading or error screen — used after Admin changes
  // and when the heartbeat reports the period/section settings changed.
  const reload = useCallback((opts) => { bootstrap(opts); }, [bootstrap]);

  const value = { ...state, startImpersonating, stopImpersonating, reload };

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}
